/**
 * Sección **Cambios** de *Control de Precios*.
 *
 * Es el detector de cambios del panel anterior (comparar los dos últimos ficheros
 * de la ubicación y repartir las diferencias en subieron, bajaron, nuevos y
 * quitados), pero con filtro de búsqueda, umbral de diferencia y —lo más
 * importante— la acción de marcar **la estación completa**, no sólo un producto.
 *
 * La tabla se queda con su propio desplazamiento (menos de la mitad de la pantalla)
 * para que la página no crezca sin fin cuando hay muchos cambios.
 */

import {
  ArrowDownRight,
  ArrowUpRight,
  Building2,
  Minus,
  Plus,
  Target,
} from 'lucide-react';
import { useMemo, useState } from 'react';

import { Alert, ICONO_EN_ALERTA } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn, formatCurrency, formatNumber } from '@/lib/utils';

import type { CambioPricing, ComparacionPricing, CompetidorPricing } from '../pricing.types';
import { SeguimientoAcciones } from './seguimiento-acciones';
import { claveCompetidor, coincideBusqueda, mensajeDe, normalizar } from '../pricing-utils';

/** Pestañas del detector de cambios, como el panel anterior. */
const PESTANAS = [
  { clave: 'aumentos', etiqueta: 'Subieron', icono: ArrowUpRight },
  { clave: 'bajadas', etiqueta: 'Bajaron', icono: ArrowDownRight },
  { clave: 'nuevos', etiqueta: 'Nuevos', icono: Plus },
  { clave: 'quitados', etiqueta: 'Quitados', icono: Minus },
] as const;

type ClavePestana = (typeof PESTANAS)[number]['clave'];

interface PreciosCambiosProps {
  comparacion?: ComparacionPricing;
  cargando: boolean;
  error: unknown;
  /** Búsqueda del filtro general de la pantalla. */
  busqueda: string;
  /** Estaciones ya marcadas por completo. */
  estacionesMarcadas: Set<string>;
  /** Competidor de cada `ESTACION|PRODUCTO` en seguimiento (para poder quitarlo). */
  porProducto: Map<string, CompetidorPricing>;
  ocupado: boolean;
  onMarcarEstacion: (estacion: string, precio?: number) => void;
  onQuitarEstacion: (estacion: string) => void;
  onMarcarProducto: (datos: {
    estacion: string;
    producto: string;
    direccion?: string;
    precio?: number;
  }) => void;
  onQuitarProducto: (competidor: CompetidorPricing) => void;
}

/** Diferencia mínima que se muestra al activar el umbral. */
const UMBRAL = 0.5;

export function PreciosCambios({
  comparacion,
  cargando,
  error,
  busqueda,
  estacionesMarcadas,
  porProducto,
  ocupado,
  onMarcarEstacion,
  onQuitarEstacion,
  onMarcarProducto,
  onQuitarProducto,
}: PreciosCambiosProps) {
  const [pestana, setPestana] = useState<ClavePestana>('aumentos');
  const [soloMayores, setSoloMayores] = useState(false);

  const lista = useMemo(() => {
    const filas: CambioPricing[] = comparacion?.[pestana] ?? [];

    return filas.filter((fila) => {
      if (!coincideBusqueda(busqueda, fila.estacion, fila.producto)) return false;
      if (!soloMayores) return true;

      const magnitud = Math.abs(fila.diferencia ?? 0);

      return magnitud >= UMBRAL;
    });
  }, [busqueda, comparacion, pestana, soloMayores]);

  if (cargando) return <Skeleton className="h-72 w-full" />;

  if (error) {
    return (
      <Alert variant="warning" title="No se pudo comparar">
        <span className={ICONO_EN_ALERTA}>
          <ArrowUpRight className="size-4" aria-hidden="true" />
          {mensajeDe(error, 'La API no pudo comparar los precios de esa ubicación.')}
        </span>
      </Alert>
    );
  }

  if (!comparacion) {
    return (
      <Card>
        <CardContent>
          <p className="text-sm text-ink-500">Elige una ubicación para comparar sus precios.</p>
        </CardContent>
      </Card>
    );
  }

  if (!comparacion.suficiente) {
    return (
      <Alert variant="info" title="Hacen falta dos consultas">
        <span className={ICONO_EN_ALERTA}>
          <Plus className="size-4" aria-hidden="true" />
          {comparacion.motivo ?? 'Esta ubicación todavía no tiene dos ficheros que comparar.'}
        </span>
      </Alert>
    );
  }

  const sinCambios = comparacion.totales.cambios === 0;

  return (
    <Card>
      <CardContent className="space-y-3">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap gap-2">
            {PESTANAS.map((item) => (
              <button
                key={item.clave}
                type="button"
                onClick={() => setPestana(item.clave)}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-control border px-3 py-1.5 text-xs font-semibold transition-colors',
                  pestana === item.clave
                    ? 'border-brand-300 bg-brand-50 text-brand-700'
                    : 'border-ink-100 text-ink-500 hover:border-ink-200 hover:text-ink-700',
                )}
              >
                <item.icono className="size-3.5" aria-hidden="true" />
                {item.etiqueta}
                <span className="font-normal text-ink-400">
                  {formatNumber(comparacion.totales[item.clave])}
                </span>
              </button>
            ))}
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-ink-400">
              {comparacion.archivo_anterior} → {comparacion.archivo_nuevo}
            </span>
            <Button
              size="sm"
              variant={soloMayores ? 'primary' : 'secondary'}
              onClick={() => setSoloMayores((previo) => !previo)}
            >
              Sólo ≥ {formatCurrency(UMBRAL)}
            </Button>
          </div>
        </header>

        {sinCambios ? (
          <p className="text-sm text-ink-500">
            No se detectaron cambios entre los dos últimos ficheros de la ubicación.
          </p>
        ) : lista.length === 0 ? (
          <p className="text-sm text-ink-500">Ningún registro coincide con el filtro actual.</p>
        ) : (
          <div className="max-h-[48vh] overflow-auto rounded-control border border-ink-50">
            <table className="w-full text-left text-sm">
              <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--color-ink-100)]">
                <tr className="border-b border-ink-100 text-xs font-semibold uppercase tracking-wider text-ink-400">
                  <th className="py-2 pl-3">Estación</th>
                  <th className="py-2">Producto</th>
                  {pestana === 'nuevos' || pestana === 'quitados' ? (
                    <th className="py-2 text-right">Precio</th>
                  ) : (
                    <>
                      <th className="py-2 text-right">Antes</th>
                      <th className="py-2 text-right">Ahora</th>
                      <th className="py-2 text-right">Diferencia</th>
                    </>
                  )}
                  <th className="w-28 py-2 pr-3 text-right">Seguimiento</th>
                </tr>
              </thead>
              <tbody>
                {lista.map((fila, indice) => {
                  const clave = claveCompetidor(fila.estacion, fila.producto);
                  const competidor = porProducto.get(clave);
                  const estacionMarcada = estacionesMarcadas.has(normalizar(fila.estacion));
                  const productoMarcado = Boolean(competidor);

                  return (
                    <tr
                      key={`${fila.estacion}-${fila.producto}-${indice}`}
                      className={cn(
                        'border-b border-ink-50 last:border-0',
                        (estacionMarcada || productoMarcado) && 'bg-emerald-50/40',
                      )}
                    >
                      <td className="py-2 pl-3 text-ink-700">{fila.estacion}</td>
                      <td className="py-2 text-ink-500">{fila.producto}</td>
                      {pestana === 'nuevos' || pestana === 'quitados' ? (
                        <td className="py-2 text-right text-ink-700">
                          {formatCurrency(fila.precio ?? 0)}
                        </td>
                      ) : (
                        <>
                          <td className="py-2 text-right text-ink-500">
                            {formatCurrency(fila.precio_anterior ?? 0)}
                          </td>
                          <td className="py-2 text-right text-ink-700">
                            {formatCurrency(fila.precio_nuevo ?? 0)}
                          </td>
                          <td
                            className={cn(
                              'py-2 text-right font-semibold',
                              (fila.diferencia ?? 0) > 0 ? 'text-rose-600' : 'text-emerald-600',
                            )}
                          >
                            {(fila.diferencia ?? 0) > 0 ? '+' : ''}
                            {formatCurrency(fila.diferencia ?? 0)}
                          </td>
                        </>
                      )}
                      <td className="py-2 pr-3 text-right">
                        <SeguimientoAcciones
                          estacion={fila.estacion}
                          producto={fila.producto}
                          estacionMarcada={estacionMarcada}
                          productoMarcado={productoMarcado}
                          ocupado={ocupado}
                          onMarcarEstacion={() =>
                            onMarcarEstacion(fila.estacion, fila.precio_anterior ?? fila.precio)
                          }
                          onQuitarEstacion={() => onQuitarEstacion(fila.estacion)}
                          onMarcarProducto={() =>
                            onMarcarProducto({
                              estacion: fila.estacion,
                              producto: fila.producto,
                              direccion: fila.direccion,
                              precio: fila.precio_anterior ?? fila.precio,
                            })
                          }
                          onQuitarProducto={() => competidor && onQuitarProducto(competidor)}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <p className="text-xs text-ink-400">
          {formatNumber(lista.length)} de {formatNumber(comparacion.totales[pestana])} registros ·{' '}
          <Building2 className="inline size-3 -translate-y-px" aria-hidden="true" /> marca o quita
          la estación completa y <Target className="inline size-3 -translate-y-px" aria-hidden="true" />{' '}
          la papelera afectan sólo a la fila; las filas en verde ya están en seguimiento.
        </p>
      </CardContent>
    </Card>
  );
}
